import * as React from 'react';
import { Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { RangedValueFilterEditProps } from '../providers/RangedValue';
import { FiltersListAddButton } from './index';
import FilterEditRequiredValidation from './FilterEditRequiredValidation';

export type RangedNumberFieldEditProps = RangedValueFilterEditProps & {
	step?: string;
};

export interface RangedNumberFieldEditState {
	fromValue: string;
	toValue: string;
	displayValidationError: boolean;
}

/**
 * Configures a range of number values in a filter. Used by both Integer and Decimal field edit components.
 */
class RangedNumberFilterEdit extends React.PureComponent<RangedNumberFieldEditProps, RangedNumberFieldEditState> {
	constructor(props) {
		super(props);

		this.state = {
			fromValue: '',
			toValue: '',
			displayValidationError: false,
		};
	}

	public componentDidMount() {
		let fromValue = '';
		let toValue = '';

		if (this.props.value) {
			fromValue = this.props.value.fromValue;
			toValue = this.props.value.toValue;

			// eslint-disable-next-line
			this.setState({
				fromValue,
				toValue,
			});
		}
	}

	/**
	 * Sends state changes up to the parent component
	 *
	 * @private
	 * @memberof RangedNumberFieldEdit
	 */
	private sendUpdatedValue = () => {
		const { fromValue, toValue } = this.state;

		const validationError = !this.props.filter.nullable && fromValue === '' && toValue === '';

		this.setState({
			displayValidationError: validationError,
		});

		if (!validationError) {
			this.props.onChange({ fromValue, toValue }, false);
		}
	};

	/**
	 * Updates local state for the from value field. Once that's done, it bubbles up the state changes.
	 *
	 * @private
	 * @memberof RangedNumberFieldEdit
	 */
	private handleFromValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ fromValue: e.currentTarget.value });
	};

	/**
	 * Updates local state for the to value field. Once that's done, it bubbles up the state changes.
	 *
	 * @private
	 * @memberof RangedNumberFieldEdit
	 */
	private handleToValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ toValue: e.currentTarget.value });
	};

	/**
	 * Allow users to hit enter to add new values.
	 *
	 * @private
	 * @memberof MultipleTextFieldEdit
	 */
	private handleOnKeyDown = (e: React.KeyboardEvent) => {
		if (e.which === 13) {
			e.preventDefault();
			this.sendUpdatedValue();
		}
	};

	public render() {
		return (
			<FormGroup>
				<Row>
					<Col sm={6}>
						<Label for="fieldValueFrom">From:</Label>
						<Input
							id="fieldValueFrom"
							type="number"
							autoFocus
							step={this.props.step}
							placeholder={this.props.filter.nullable ? '<Empty>' : 'Enter a value'}
							value={this.state.fromValue}
							onChange={this.handleFromValueChange}
							onKeyDown={this.handleOnKeyDown}
						/>
					</Col>
					<Col sm={6}>
						<Label for="fieldValueTo">To:</Label>
						<Input
							id="fieldValueTo"
							type="number"
							step={this.props.step}
							placeholder={this.props.filter.nullable ? '<Empty>' : 'Enter a value'}
							value={this.state.toValue}
							onChange={this.handleToValueChange}
							onKeyDown={this.handleOnKeyDown}
						/>
					</Col>
				</Row>
				<div className="filters-popover__actions">
					<FiltersListAddButton onClick={this.sendUpdatedValue} />
				</div>
				{this.state.displayValidationError ? (
					<FilterEditRequiredValidation messageOverride="Number field is required" />
				) : null}
			</FormGroup>
		);
	}
}

export default RangedNumberFilterEdit;
